import { useState, useEffect, useMemo } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';

interface CompanyEvent {
  id: string;
  title: string;
  description: string | null;
  eventType: string;
  startTime: string;
  endTime: string | null;
  allDay: boolean;
  recurrenceRule: string | null;
  location: string | null;
  virtualLink: string | null;
  visibility: string;
  dealerIds: string[];
  employeeIds: string[];
  department: string | null;
  attendeeDealerId: string | null;
  attendeeDealerContactId: string | null;
  attendeeEmployeeIds: string[];
  status: string;
  cancelledReason: string | null;
  color: string | null;
  createdAt: string;
  attendeeDealerName?: string;
  attendeeContactName?: string;
}

interface Dealer {
  id: string;
  company_name: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  department?: string;
}

const EVENT_TYPES = [
  { value: 'holiday', label: 'Holiday', color: '#dc2626' },
  { value: 'team_meeting', label: 'Team Meeting', color: '#2563eb' },
  { value: 'dealer_meeting', label: 'Dealer Meeting', color: '#059669' },
  { value: 'training', label: 'Training', color: '#7c3aed' },
  { value: 'production', label: 'Production', color: '#ea580c' },
  { value: 'shipping', label: 'Shipping', color: '#0891b2' },
  { value: 'sales', label: 'Sales', color: '#16a34a' },
  { value: 'announcement', label: 'Announcement', color: '#ca8a04' },
  { value: 'other', label: 'Other', color: '#6b7280' },
];

const VISIBILITY_OPTIONS = [
  { value: 'all', label: 'Everyone' },
  { value: 'employees_only', label: 'Employees Only' },
  { value: 'dealers_only', label: 'Dealers Only' },
  { value: 'department', label: 'Specific Department' },
  { value: 'specific', label: 'Specific People' },
];

const DEPARTMENTS = [
  'production',
  'shipping',
  'sales',
  'accounting',
  'admin',
];

interface CompanyHoliday {
  name: string;
  month: number; // 1-12
  day: number;   // 1-31 for fixed, 0 for floating
  rule?: string; // e.g. "4th_thu" for Thanksgiving
  halfDay?: boolean;
}

const DEFAULT_HOLIDAYS: CompanyHoliday[] = [
  { name: "New Year's Day", month: 1, day: 1 },
  { name: 'Memorial Day', month: 5, day: 0, rule: 'last_mon' },
  { name: 'Independence Day', month: 7, day: 4 },
  { name: 'Labor Day', month: 9, day: 0, rule: '1st_mon' },
  { name: 'Thanksgiving', month: 11, day: 0, rule: '4th_thu' },
  { name: 'Day After Thanksgiving', month: 11, day: 0, rule: '4th_fri' },
  { name: 'Christmas Eve', month: 12, day: 24 },
  { name: 'Christmas Day', month: 12, day: 25 },
  { name: "New Year's Eve", month: 12, day: 31, halfDay: true },
];

function getFloatingDate(year: number, month: number, rule: string): number {
  const dayMap: Record<string, number> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  const match = rule.match(/^(1st|2nd|3rd|4th|5th|last)_(sun|mon|tue|wed|thu|fri|sat)$/);
  if (!match) return 1;
  const [, ordinal, dayName] = match;
  const targetDay = dayMap[dayName];

  if (ordinal === 'last') {
    const lastDate = new Date(year, month, 0); // last day of month
    let d = lastDate.getDate();
    while (new Date(year, month - 1, d).getDay() !== targetDay) d--;
    return d;
  }
  const nth = parseInt(ordinal);
  let count = 0;
  for (let d = 1; d <= 31; d++) {
    const dt = new Date(year, month - 1, d);
    if (dt.getMonth() !== month - 1) break;
    if (dt.getDay() === targetDay) {
      count++;
      if (count === nth) return d;
    }
  }
  return 1;
}

function getHolidayDate(h: CompanyHoliday, year: number): Date {
  if (h.day > 0) return new Date(year, h.month - 1, h.day);
  return new Date(year, h.month - 1, getFloatingDate(year, h.month, h.rule || '1st_mon'));
}

export default function CompanyCalendarPage() {
  const [events, setEvents] = useState<CompanyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CompanyEvent | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');

  // Company holidays
  const [companyHolidays, setCompanyHolidays] = useState<CompanyHoliday[]>([]);
  const [showHolidaysPanel, setShowHolidaysPanel] = useState(false);
  const [newHolidayName, setNewHolidayName] = useState('');
  const [newHolidayMonth, setNewHolidayMonth] = useState(1);
  const [newHolidayDay, setNewHolidayDay] = useState(1);
  const [newHolidayHalf, setNewHolidayHalf] = useState(false);
  const [holidaysSaving, setHolidaysSaving] = useState(false);
  const [holidaysLoaded, setHolidaysLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state for new/edit event
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventType: 'team_meeting',
    startTime: '',
    endTime: '',
    allDay: false,
    location: '',
    virtualLink: '',
    visibility: 'all',
    department: '',
    dealerIds: [] as string[],
    employeeIds: [] as string[],
    attendeeDealerId: '',
    color: '',
  });

  useEffect(() => {
    loadEvents();
    loadDealers();
    loadEmployees();
    if (!holidaysLoaded) loadCompanyHolidays();
  }, [currentMonth]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0, 23, 59, 59);

      const data = await api.companyEvents.getAllEvents({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
      setEvents(data);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDealers = async () => {
    try {
      const data = await api.dealers.list();
      setDealers(data);
    } catch (error) {
      console.error('Failed to load dealers:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await api.employees.list();
      setEmployees(data);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  const loadCompanyHolidays = async () => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/system-settings/company-holidays`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCompanyHolidays(Array.isArray(data) && data.length > 0 ? data : DEFAULT_HOLIDAYS);
      } else {
        setCompanyHolidays(DEFAULT_HOLIDAYS);
      }
      setHolidaysLoaded(true);
    } catch {
      setCompanyHolidays(DEFAULT_HOLIDAYS);
      setHolidaysLoaded(true);
    }
  };

  const saveCompanyHolidays = async (holidays: CompanyHoliday[]) => {
    setHolidaysSaving(true);
    try {
      const token = localStorage.getItem('admin_auth_token');
      await fetch(`${import.meta.env.VITE_API_URL || '/api'}/system-settings/company-holidays`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(holidays),
      });
      setCompanyHolidays(holidays);
    } catch {
      // silent
    } finally {
      setHolidaysSaving(false);
    }
  };

  const addCustomHoliday = () => {
    if (!newHolidayName.trim()) return;
    const newH: CompanyHoliday = { name: newHolidayName.trim(), month: newHolidayMonth, day: newHolidayDay };
    if (newHolidayHalf) newH.halfDay = true;
    const updated = [...companyHolidays, newH];
    saveCompanyHolidays(updated);
    setNewHolidayName('');
    setNewHolidayDay(1);
    setNewHolidayHalf(false);
  };

  const removeHoliday = (index: number) => {
    const updated = companyHolidays.filter((_, i) => i !== index);
    saveCompanyHolidays(updated);
  };

  // Get holidays for a specific day (for calendar display)
  const getHolidaysForDay = (date: Date) => {
    return companyHolidays.filter(h => {
      const hd = getHolidayDate(h, date.getFullYear());
      return hd.getMonth() === date.getMonth() && hd.getDate() === date.getDate();
    });
  };

  const handleCreateEvent = async () => {
    try {
      setSaving(true);
      await api.companyEvents.create({
        ...formData,
        dealerIds: formData.visibility === 'specific' ? formData.dealerIds : [],
        employeeIds: formData.visibility === 'specific' ? formData.employeeIds : [],
        department: formData.visibility === 'department' ? formData.department : null,
        attendeeDealerId: formData.eventType === 'dealer_meeting' ? formData.attendeeDealerId : null,
      });
      setShowNewEventModal(false);
      resetForm();
      loadEvents();
    } catch (error) {
      console.error('Failed to create event:', error);
      alert('Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEvent = async () => {
    if (!selectedEvent) return;
    try {
      setSaving(true);
      await api.companyEvents.update(selectedEvent.id, {
        ...formData,
        dealerIds: formData.visibility === 'specific' ? formData.dealerIds : [],
        employeeIds: formData.visibility === 'specific' ? formData.employeeIds : [],
        department: formData.visibility === 'department' ? formData.department : null,
        attendeeDealerId: formData.eventType === 'dealer_meeting' ? formData.attendeeDealerId : null,
      });
      setShowEventModal(false);
      setSelectedEvent(null);
      resetForm();
      loadEvents();
    } catch (error) {
      console.error('Failed to update event:', error);
      alert('Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEvent = async () => {
    if (!selectedEvent) return;
    const reason = prompt('Cancellation reason (optional):');
    try {
      await api.companyEvents.cancel(selectedEvent.id, reason || undefined);
      setShowEventModal(false);
      setSelectedEvent(null);
      loadEvents();
    } catch (error) {
      console.error('Failed to cancel event:', error);
      alert('Failed to cancel event');
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    if (!confirm('Are you sure you want to permanently delete this event?')) return;
    try {
      await api.companyEvents.delete(selectedEvent.id);
      setShowEventModal(false);
      setSelectedEvent(null);
      loadEvents();
    } catch (error) {
      console.error('Failed to delete event:', error);
      alert('Failed to delete event');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      eventType: 'team_meeting',
      startTime: '',
      endTime: '',
      allDay: false,
      location: '',
      virtualLink: '',
      visibility: 'all',
      department: '',
      dealerIds: [],
      employeeIds: [],
      attendeeDealerId: '',
      color: '',
    });
  };

  const openEditModal = (event: CompanyEvent) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      eventType: event.eventType,
      startTime: event.startTime ? new Date(event.startTime).toISOString().slice(0, 16) : '',
      endTime: event.endTime ? new Date(event.endTime).toISOString().slice(0, 16) : '',
      allDay: event.allDay,
      location: event.location || '',
      virtualLink: event.virtualLink || '',
      visibility: event.visibility,
      department: event.department || '',
      dealerIds: event.dealerIds || [],
      employeeIds: event.employeeIds || [],
      attendeeDealerId: event.attendeeDealerId || '',
      color: event.color || '',
    });
    setShowEventModal(true);
  };

  const openNewEventModal = (date?: Date) => {
    resetForm();
    if (date) {
      const dateStr = date.toISOString().slice(0, 16);
      setFormData(prev => ({ ...prev, startTime: dateStr }));
    }
    setShowNewEventModal(true);
  };

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days: (Date | null)[] = [];

    // Add empty slots for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const getEventsForDay = (date: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.startTime);
      return (
        eventDate.getFullYear() === date.getFullYear() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getDate() === date.getDate()
      );
    });
  };

  const filteredEvents = useMemo(() => {
    if (filterType === 'all') return events;
    return events.filter(e => e.eventType === filterType);
  }, [events, filterType]);

  const getEventColor = (eventType: string, customColor?: string | null) => {
    if (customColor) return customColor;
    return EVENT_TYPES.find(t => t.value === eventType)?.color || '#6b7280';
  };

  const days = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div>
      <PageHeader
        title="Company Calendar"
        subtitle="Manage company events, holidays, meetings, and announcements"
        actions={
          <button
            onClick={() => openNewEventModal()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            + New Event
          </button>
        }
      />

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">All Events</option>
              {EVENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2 ml-auto">
            {EVENT_TYPES.map(type => (
              <div key={type.value} className="flex items-center gap-1 text-sm">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: type.color }}
                />
                <span className="text-gray-600">{type.label}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Company Holidays Panel */}
      <Card className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-600" />
            <h3 className="font-semibold text-gray-900">Company Holidays</h3>
            <span className="text-sm text-gray-500">
              ({companyHolidays.filter(h => !h.halfDay).length} closed{companyHolidays.some(h => h.halfDay) ? `, ${companyHolidays.filter(h => h.halfDay).length} half day` : ''})
            </span>
          </div>
          <button
            onClick={() => setShowHolidaysPanel(!showHolidaysPanel)}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            {showHolidaysPanel ? 'Hide' : 'Manage'}
          </button>
        </div>

        {showHolidaysPanel && (
          <div className="mt-4 border-t pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
              {companyHolidays.map((h, i) => {
                const hDate = getHolidayDate(h, currentMonth.getFullYear());
                const dateStr = hDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                return (
                  <div key={i} className={`flex items-center justify-between rounded-lg px-3 py-2 ${h.halfDay ? 'bg-orange-50 border border-orange-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900">{h.name}</span>
                      <span className="text-xs text-gray-500 ml-2">{dateStr}{h.rule ? ' (floating)' : ''}</span>
                      {h.halfDay && <span className="text-xs text-orange-600 ml-1 font-medium">½ day</span>}
                    </div>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <button
                        onClick={() => {
                          const updated = [...companyHolidays];
                          updated[i] = { ...updated[i], halfDay: !updated[i].halfDay };
                          saveCompanyHolidays(updated);
                        }}
                        className={`text-xs px-2 py-0.5 rounded ${h.halfDay ? 'bg-orange-200 text-orange-700' : 'bg-gray-200 text-gray-600'} hover:opacity-80`}
                        title={h.halfDay ? 'Switch to full day closed' : 'Switch to half day'}
                      >
                        {h.halfDay ? '½' : 'Full'}
                      </button>
                      <button
                        onClick={() => removeHoliday(i)}
                        className="text-red-400 hover:text-red-600 text-sm"
                        title="Remove"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2 items-end">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Holiday Name</label>
                <input
                  type="text"
                  value={newHolidayName}
                  onChange={e => setNewHolidayName(e.target.value)}
                  placeholder="e.g., Company Retreat"
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-48"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Month</label>
                <select
                  value={newHolidayMonth}
                  onChange={e => setNewHolidayMonth(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                >
                  {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Day</label>
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={newHolidayDay}
                  onChange={e => setNewHolidayDay(parseInt(e.target.value) || 1)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-16"
                />
              </div>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newHolidayHalf}
                  onChange={e => setNewHolidayHalf(e.target.checked)}
                  className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-600">Half day</span>
              </label>
              <button
                onClick={addCustomHoliday}
                disabled={!newHolidayName.trim() || holidaysSaving}
                className="px-4 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Add Holiday
              </button>
            </div>
            {holidaysSaving && <p className="text-xs text-gray-400 mt-2">Saving...</p>}
          </div>
        )}
      </Card>

      {/* Calendar */}
      <Card>
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className="text-xl font-semibold text-gray-900">{monthName}</h2>
          <button
            onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-px mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
              {days.map((day, idx) => (
                <div
                  key={idx}
                  className={`min-h-[120px] bg-white p-2 ${
                    day ? 'cursor-pointer hover:bg-gray-50' : ''
                  }`}
                  onClick={() => day && openNewEventModal(day)}
                >
                  {day && (
                    <>
                      <div className={`text-sm font-medium mb-1 ${
                        day.toDateString() === new Date().toDateString()
                          ? 'text-blue-600'
                          : 'text-gray-900'
                      }`}>
                        {day.getDate()}
                      </div>
                      <div className="space-y-1">
                        {getHolidaysForDay(day).map((h, hi) => (
                          <div
                            key={`h-${hi}`}
                            className={`text-xs p-1 rounded truncate font-medium ${h.halfDay ? 'text-orange-700 border border-orange-300' : 'text-white'}`}
                            style={{ backgroundColor: h.halfDay ? '#fff7ed' : '#dc2626' }}
                            title={`${h.name} — ${h.halfDay ? 'Half Day' : 'Company Closed'}`}
                          >
                            {h.halfDay ? '½ ' : ''}{h.name}
                          </div>
                        ))}
                        {getEventsForDay(day)
                          .filter(e => filterType === 'all' || e.eventType === filterType)
                          .slice(0, 3 - getHolidaysForDay(day).length)
                          .map(event => (
                          <div
                            key={event.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(event);
                            }}
                            className="text-xs p-1 rounded truncate text-white cursor-pointer hover:opacity-80"
                            style={{ backgroundColor: getEventColor(event.eventType, event.color) }}
                            title={event.title}
                          >
                            {event.allDay ? '' : new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' '}
                            {event.title}
                          </div>
                        ))}
                        {getEventsForDay(day).filter(e => filterType === 'all' || e.eventType === filterType).length > 3 && (
                          <div className="text-xs text-gray-500 pl-1">
                            +{getEventsForDay(day).filter(e => filterType === 'all' || e.eventType === filterType).length - 3} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* Upcoming Events List */}
      <Card className="mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Events This Month</h3>
        {filteredEvents.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No events this month</p>
        ) : (
          <div className="divide-y">
            {filteredEvents.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()).map(event => (
              <div
                key={event.id}
                className="py-3 flex items-center gap-4 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded"
                onClick={() => openEditModal(event)}
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getEventColor(event.eventType, event.color) }}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">{event.title}</div>
                  <div className="text-sm text-gray-500">
                    {new Date(event.startTime).toLocaleDateString()} {!event.allDay && new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {event.location && ` • ${event.location}`}
                  </div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 capitalize">
                  {event.eventType.replace('_', ' ')}
                </span>
                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-600 capitalize">
                  {event.visibility.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* New Event Modal */}
      {showNewEventModal && (
        <EventModal
          title="Create New Event"
          formData={formData}
          setFormData={setFormData}
          onClose={() => { setShowNewEventModal(false); resetForm(); }}
          onSave={handleCreateEvent}
          saving={saving}
          dealers={dealers}
          employees={employees}
        />
      )}

      {/* Edit Event Modal */}
      {showEventModal && selectedEvent && (
        <EventModal
          title="Edit Event"
          formData={formData}
          setFormData={setFormData}
          onClose={() => { setShowEventModal(false); setSelectedEvent(null); resetForm(); }}
          onSave={handleUpdateEvent}
          onCancel={handleCancelEvent}
          onDelete={handleDeleteEvent}
          saving={saving}
          dealers={dealers}
          employees={employees}
          isEdit
        />
      )}
    </div>
  );
}

// Event Modal Component
function EventModal({
  title,
  formData,
  setFormData,
  onClose,
  onSave,
  onCancel,
  onDelete,
  saving,
  dealers,
  employees,
  isEdit = false,
}: {
  title: string;
  formData: any;
  setFormData: (data: any) => void;
  onClose: () => void;
  onSave: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
  saving: boolean;
  dealers: Dealer[];
  employees: Employee[];
  isEdit?: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">{title}</h2>
        </div>
        <div className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Event title"
            />
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type *</label>
            <select
              value={formData.eventType}
              onChange={(e) => setFormData({ ...formData, eventType: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              {EVENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Date/Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start *</label>
              <input
                type={formData.allDay ? 'date' : 'datetime-local'}
                value={formData.allDay ? formData.startTime?.slice(0, 10) : formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
              <input
                type={formData.allDay ? 'date' : 'datetime-local'}
                value={formData.allDay ? formData.endTime?.slice(0, 10) : formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>

          {/* All Day */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allDay"
              checked={formData.allDay}
              onChange={(e) => setFormData({ ...formData, allDay: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="allDay" className="text-sm text-gray-700">All day event</label>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              rows={3}
              placeholder="Event description"
            />
          </div>

          {/* Location / Virtual Link */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Physical location"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Virtual Link</label>
              <input
                type="url"
                value={formData.virtualLink}
                onChange={(e) => setFormData({ ...formData, virtualLink: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="https://zoom.us/..."
              />
            </div>
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Who Can See This? *</label>
            <select
              value={formData.visibility}
              onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              {VISIBILITY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Department (if visibility = department) */}
          {formData.visibility === 'department' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Select department</option>
                {DEPARTMENTS.map(dept => (
                  <option key={dept} value={dept}>{dept.charAt(0).toUpperCase() + dept.slice(1)}</option>
                ))}
              </select>
            </div>
          )}

          {/* Dealer selector (for dealer meetings) */}
          {formData.eventType === 'dealer_meeting' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dealer</label>
              <select
                value={formData.attendeeDealerId}
                onChange={(e) => setFormData({ ...formData, attendeeDealerId: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Select dealer</option>
                {dealers.map(dealer => (
                  <option key={dealer.id} value={dealer.id}>{dealer.company_name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Custom Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Color (optional)</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.color || EVENT_TYPES.find(t => t.value === formData.eventType)?.color || '#6b7280'}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <button
                type="button"
                onClick={() => setFormData({ ...formData, color: '' })}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Reset to default
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t flex justify-between">
          <div>
            {isEdit && (
              <div className="flex gap-2">
                <button
                  onClick={onCancel}
                  className="px-4 py-2 text-orange-600 hover:bg-orange-50 rounded-lg"
                >
                  Cancel Event
                </button>
                <button
                  onClick={onDelete}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Close
            </button>
            <button
              onClick={onSave}
              disabled={saving || !formData.title || !formData.startTime}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : isEdit ? 'Update Event' : 'Create Event'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
